import { useCallback } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useGlobalStore } from '@highlight/ipc/react'
import { isAlphaOrDynamicChannel } from '@shared/utils/builds/channels.shared'

import { useMessageMentions } from '../components'
import { useAuth } from '../providers'
import { getCurrentChatInput, useActiveSkillComparisonIds, useBindLocalSide, useOpenLocalPair } from '../store'
import type { SendMessageOptions } from '../types'
import {
  createLiveComparison,
  getSkillComparison,
  listSkillCandidates,
  SkillCompareRequestError,
  type SkillComparisonDetail,
  type SkillCandidateListing,
  type SkillCompareSide,
} from './skill-compare-api'
import { ambiguousCandidates, candidatesForMentions, mentionedSkillId } from './skill-compare-plan'
import { useSubmitMessage } from './useSubmitMessage'
import { useAddChatToast } from '../store/toasts'

/** Settings-store key behind the Developers tab toggle; on by default for whoever the gateway lets in. */
export const SKILL_COMPARE_MODE_KEY = 'dev.skillCompareMode'
export const SKILL_COMPARE_MODE_DEFAULT = true

/** Alpha and PR builds only; a beta or production build never asks, never shows, never runs compare. */
const SKILL_COMPARE_AVAILABLE = isAlphaOrDynamicChannel()

export const SKILL_COMPARISON_QUERY_KEY = 'skill-comparison'

/** Fast until both sides' chats are known (the bar needs them to switch sides), then just watching for a pick made elsewhere. */
const COMPARISON_POLL_MS = 3000
const COMPARISON_IDLE_POLL_MS = 60_000

type SkillCompareAccess = { allowed: boolean; candidates: Array<SkillCandidateListing> }

const NO_ACCESS: SkillCompareAccess = { allowed: false, candidates: [] }

/** The gateway's answer: it lists the active candidates when the skill-compare flag is on and 403s everyone else. */
function accessQuery(getAuthToken: () => Promise<string>) {
  return {
    queryKey: ['skill-candidates'],
    queryFn: (): Promise<SkillCompareAccess> =>
      listSkillCandidates(getAuthToken).then(
        (candidates) => ({ allowed: true, candidates }),
        (error: unknown) => {
          // Only a refusal is an answer; a blip throws so the last answer stays cached and the query retries.
          if (error instanceof SkillCompareRequestError && (error.status === 401 || error.status === 403)) {
            return NO_ACCESS
          }
          throw error
        },
      ),
    staleTime: 5 * 60 * 1000,
  }
}

export function useSkillCompareAccess(): SkillCompareAccess {
  const { getAuthToken } = useAuth()
  const { data } = useQuery({ ...accessQuery(getAuthToken), enabled: SKILL_COMPARE_AVAILABLE })
  return data ?? NO_ACCESS
}

/** On when the build and the gateway allow it, unless the Developers toggle was turned off. */
export function useSkillCompareMode(): boolean {
  const [enabled] = useGlobalStore<boolean>('settings', SKILL_COMPARE_MODE_KEY, SKILL_COMPARE_MODE_DEFAULT)
  return useSkillCompareAccess().allowed && enabled
}

function isGone(error: unknown): boolean {
  return error instanceof SkillCompareRequestError && error.status === 404
}

/** Every comparison this session started, polled until each pair resolves or the comparison is gone. */
export function useActiveSkillComparisons(): Array<SkillComparisonDetail> {
  const ids = useActiveSkillComparisonIds()
  const { getAuthToken } = useAuth()
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [SKILL_COMPARISON_QUERY_KEY, id],
      queryFn: () => getSkillComparison(id, getAuthToken),
      // A 404 means the comparison was deleted on the dashboard; nothing will ever resolve it.
      retry: (count: number, error: unknown) => !isGone(error) && count < 3,
      refetchInterval: (query: { state: { data?: SkillComparisonDetail; error: unknown } }) => {
        const pairs = query.state.data?.pairs
        if (isGone(query.state.error) || pairs?.every((pair) => pair.resolvedBy)) return false
        return pairs?.every((pair) => pair.old.conversationId && pair.new.conversationId)
          ? COMPARISON_IDLE_POLL_MS
          : COMPARISON_POLL_MS
      },
    })),
    // Structurally shared, so the list is referentially stable however many ids there are.
    combine: (results) => results.map((result) => result.data).filter((detail) => !!detail),
  })
}

// Module-level, not a ref: the Enter handler and the send button each mount the composer hook.
let opening = false

/**
 * Submits the composer as both sides of a live pair when its skill mention has an active candidate.
 * Resolves false when the turn is not a comparison, so the caller submits normally.
 */
export function useSkillCompareSubmit(): (options: SendMessageOptions) => Promise<boolean> {
  const mode = useSkillCompareMode()
  const queryClient = useQueryClient()
  const mentions = useMessageMentions()
  const { getAuthToken } = useAuth()
  const openPair = useOpenLocalPair()
  const bindSide = useBindLocalSide()
  const submitMessage = useSubmitMessage()
  const addChatToast = useAddChatToast()
  return useCallback(
    async (options) => {
      if (!mode || opening) return opening
      // Captured before the pair opens, so a second Enter during that round trip cannot split the sides.
      const input = getCurrentChatInput()
      if (!input.trim() || !mentionedSkillId(mentions)) return false
      opening = true
      let pair: SkillComparisonDetail['pairs'][number] | undefined
      let comparison: SkillComparisonDetail | undefined
      try {
        // Fresh list: a candidate activated on the dashboard a moment ago must compare on this very turn.
        const { candidates } = await queryClient.fetchQuery({ ...accessQuery(getAuthToken), staleTime: 0 })
        const matching = candidatesForMentions(mentions, candidates)
        if (matching.length === 0) return false
        if (matching.length > 1) {
          addChatToast({ title: 'Skill compare skipped', message: ambiguousCandidates(matching), type: 'error' })
          return false
        }
        comparison = await createLiveComparison(matching[0], getAuthToken)
        pair = comparison.pairs[0]
      } catch (error) {
        console.error('[skill-compare] could not open a live pair; submitting normally', error)
      } finally {
        opening = false
      }
      if (!pair || !comparison) return false
      const { id: pairId, leftIsNew } = pair
      openPair({ pairId, comparisonId: comparison.id, skillSlug: comparison.skillSlug, leftIsNew })
      // The left side runs in the chat the dev is in; the other in a background chat. Each records its chat
      // as it starts, so the compare bar is there from the first token.
      const here: SkillCompareSide = leftIsNew ? 'new' : 'old'
      const other: SkillCompareSide = here === 'new' ? 'old' : 'new'
      const shared = { mentions: [...mentions] }
      void submitMessage(input, {
        ...options,
        ...shared,
        skillCompare: { pairId, side: here },
        onSubmit: (conversationId) => {
          options.onSubmit?.(conversationId)
          bindSide(pairId, here, conversationId)
        },
      })
      void submitMessage(input, {
        ...shared,
        isNewChat: true,
        preserveDraft: true,
        preserveActiveChat: true,
        preservePendingMessage: true,
        skillCompare: { pairId, side: other },
        onSubmit: (conversationId) => bindSide(pairId, other, conversationId),
      }).catch((error: unknown) => console.error('[skill-compare] side B failed to start', error))
      return true
    },
    [mode, queryClient, mentions, getAuthToken, openPair, bindSide, submitMessage, addChatToast],
  )
}
